/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ScenarioData } from '@inbox-demo-nx/types';
import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';

export const ScenarioContext = createContext<ScenarioData>({} as any);

type Callback = (...args: any[]) => void;
export const ScenarioCallbackContext = createContext<{
  addCallback: (key: string, callback: Callback) => void;
  removeCallback: (key: string) => void;
  runCallback: (key: string) => void;
}>({} as any);

export function ScenarioContextProvider({
  children,
  scenario,
}: {
  children: ReactNode;
  scenario: ScenarioData;
}) {
  const callbackMapRef = useRef(new Map<string, () => void>());

  const scenarioCallbackContextValue = useMemo(() => {
    function addCallback(key: string, callback: Callback) {
      if (callbackMapRef.current.has(key)) {
        throw new Error('Duplicated key');
      }
      callbackMapRef.current.set(key, callback);
    }

    function removeCallback(key: string) {
      callbackMapRef.current.delete(key);
    }

    function runCallback(key: string, ...args: any[]) {
      return callbackMapRef.current.get(key)?.(...(args as []));
    }

    return { addCallback, removeCallback, runCallback };
  }, []);

  return (
    <ScenarioContext.Provider value={scenario}>
      <ScenarioCallbackContext.Provider value={scenarioCallbackContextValue}>
        {children}
      </ScenarioCallbackContext.Provider>
    </ScenarioContext.Provider>
  );
}

export const useScenarioContext = () => useContext(ScenarioContext);

export const useScenarioCallbackContext = () =>
  useContext(ScenarioCallbackContext);
